import React, { Component } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4PluginsForceDirected from '@amcharts/amcharts4/plugins/forceDirected';
import _isEqual from 'lodash/isEqual';

interface Track {
    name: string;
    plays: number;
    earnings: number;
}

interface Props {
    data: Track[];
    valueKey: 'plays' | 'earnings';
}

const chartDivId = 'plays-or-earnings-per-track';

export class PlaysOrEarningsPerTrack extends Component<Props> {
    private chart: am4PluginsForceDirected.ForceDirectedTree | null = null;

    private networkSeries: am4PluginsForceDirected.ForceDirectedSeries | null =
        null;

    componentDidMount(): void {
        const { data, valueKey } = this.props;
        const chart = am4core.create(
            chartDivId,
            am4PluginsForceDirected.ForceDirectedTree,
        );
        const networkSeries = chart.series.push(
            new am4PluginsForceDirected.ForceDirectedSeries(),
        );

        this.getTooltipText = this.getTooltipText.bind(this);

        chart.numberFormatter.numberFormat = '#,###.00';

        networkSeries.data = data;
        networkSeries.dataFields.name = 'name';
        networkSeries.dataFields.value = valueKey;
        networkSeries.links.template.distance = 1;
        networkSeries.nodes.template.adapter.add(
            'tooltipText',
            this.getTooltipText,
        );
        networkSeries.nodes.template.fillOpacity = 1;
        networkSeries.nodes.template.outerCircle.scale = 1;
        networkSeries.nodes.template.label.text = '{name}';
        networkSeries.fontSize = 12;
        networkSeries.nodes.template.label.hideOversized = true;
        networkSeries.nodes.template.label.truncate = true;
        networkSeries.minRadius = am4core.percent(2);
        networkSeries.maxRadius = am4core.percent(10);
        networkSeries.manyBodyStrength = -5;
        networkSeries.links.template.strokeOpacity = 0;

        this.chart = chart;
        this.networkSeries = networkSeries;
    }

    componentDidUpdate(prevProps: Props): void {
        const { data, valueKey } = this.props;

        if (this.networkSeries !== null) {
            if (!_isEqual(prevProps.data, data)) {
                this.networkSeries.data = data;
            }
            if (prevProps.valueKey !== valueKey) {
                // TODO: I'm trying to update the networkSeries.dataFields.value to the new `valueKey`
                // so that this chart can be switched back and forth between Plays per Track and
                // Earnings per Track, but the update is not reliably causing the chart to rerender.
                this.networkSeries.bindDataField('value', valueKey);
                this.networkSeries.applyCurrentState();
            }
        }
    }

    componentWillUnmount(): void {
        if (this.chart) {
            this.chart.dispose();
        }
    }

    getTooltipText(): string {
        const { valueKey } = this.props;

        if (valueKey === 'earnings') {
            // eslint-disable-next-line no-template-curly-in-string
            return '{name}: ${value}';
        }

        if (valueKey === 'plays') {
            return '{name}: {value} plays';
        }

        return '{name}';
    }

    render(): JSX.Element {
        return (
            <div id={chartDivId} style={{ width: '100%', height: '500px' }} />
        );
    }
}
